import { useEffect, useMemo, useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { getDealerReport } from "../../../services/dealerAnalytics";
import { Sparkline } from "./charts";

// ---- Formatting ----------------------------------------------------------

export const fmtNum = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? "–" : Number(n).toLocaleString("en-US"));

export const fmtCompact = (n) => {
  if (n === null || n === undefined) return "–";
  const v = Number(n);
  if (Math.abs(v) < 1000) return String(Math.round(v * 10) / 10);
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v);
};

export const fmtUsd = (n) => (n === null || n === undefined ? "–" : `$${Math.round(Number(n)).toLocaleString("en-US")}`);

export const fmtUsdCompact = (n) => {
  if (n === null || n === undefined) return "–";
  const v = Number(n);
  if (Math.abs(v) < 1000) return `$${Math.round(v)}`;
  return `$${new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(v)}`;
};

export const fmtHours = (h) => {
  if (h === null || h === undefined) return "–";
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${Math.round(h * 10) / 10} h`;
  const days = h / 24;
  return `${days < 10 ? Math.round(days * 10) / 10 : Math.round(days)} days`;
};

export const pct = (part, whole, places = 1) => {
  if (!whole) return "–";
  const f = 10 ** places;
  return `${Math.round((part / whole) * 100 * f) / f}%`;
};

const TZ = "America/New_York";

// "Oct 3" for a YYYY-MM-DD key (read as a calendar date, not a UTC instant).
export const fmtDayKey = (key, withYear = false) => {
  const [y, m, d] = String(key).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });
};

// "Oct 1 – Oct 15, 2026", or with both years when the range spans two.
export const fmtRange = (fromKey, toKey) => {
  const sameYear = String(fromKey).slice(0, 4) === String(toKey).slice(0, 4);
  return `${fmtDayKey(fromKey, !sameYear)} – ${fmtDayKey(toKey, true)}`;
};

// How a report's own `window` names this period and the one it is compared with.
export const periodLabels = (w) => ({
  current: w.custom ? fmtRange(w.from, w.to) : `Last ${w.days} days`,
  previous: `${w.custom ? "" : `Previous ${w.days} days, `}${fmtRange(w.previousFrom, w.previousTo)}`,
});

export const fmtMonthKey = (key) => {
  const [y, m] = String(key).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ }) : "–");

export const timeAgo = (iso) => {
  if (!iso) return "–";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 60 ? `${days}d ago` : fmtDate(iso);
};

export const waitedFor = (iso) => {
  if (!iso) return "–";
  const hours = (Date.now() - new Date(iso).getTime()) / 3600000;
  return fmtHours(hours);
};

// ---- Loading reports -------------------------------------------------------

// Reports already fetched this session, so moving between tabs is instant.
// A refresh (or a new period) fetches again; entries are dropped after a few
// minutes so a tab left open does not show stale numbers forever.
const CACHE_MS = 5 * 60 * 1000;
const reportCache = new Map();

export const clearReportCache = () => reportCache.clear();

/**
 * { data, loading, error } for one report. `range` is the query naming the
 * period: "days=30" or "from=2026-10-01&to=2026-10-15". While a new period loads, the
 * previous data stays on screen (dimmed by the caller) rather than flashing
 * empty.
 */
export const useReport = (report, range, refreshKey) => {
  const [state, setState] = useState(() => {
    const hit = reportCache.get(`${report}:${range}`);
    return { data: hit ? hit.data : null, loading: !hit, error: "" };
  });

  useEffect(() => {
    let alive = true;
    const key = `${report}:${range}`;
    const hit = reportCache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) {
      setState({ data: hit.data, loading: false, error: "" });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true, error: "" }));
    getDealerReport(report, range)
      .then((res) => {
        if (!alive) return;
        if (res && res.message === "Success") {
          reportCache.set(key, { data: res.data, at: Date.now() });
          setState({ data: res.data, loading: false, error: "" });
        } else {
          setState((s) => ({ ...s, loading: false, error: "This report could not be loaded." }));
        }
      })
      .catch(() => alive && setState((s) => ({ ...s, loading: false, error: "This report could not be loaded." })));
    return () => { alive = false; };
  }, [report, range, refreshKey]);

  return state;
};

/** The frame every tab renders in: loading, error and dimmed-refetch states. */
export const ReportFrame = ({ state, children }) => {
  if (!state.data && state.loading) return <div className={styles.loading}>Loading…</div>;
  if (!state.data) return <div className={styles.error}>{state.error || "Nothing to show."}</div>;
  return (
    <div className={state.loading ? styles.dimmed : undefined}>
      {state.error && <div className={styles.error} style={{ marginBottom: 14 }}>{state.error} Showing the last numbers loaded.</div>}
      {children(state.data)}
    </div>
  );
};

// ---- Building blocks -------------------------------------------------------

export const Card = ({ title, sub, actions, children, className = "" }) => (
  <section className={`${styles.card} ${className}`}>
    {(title || actions) && (
      <div className={styles.cardHead}>
        <div>
          {title && <h3 className={styles.cardTitle}>{title}</h3>}
          {sub && <p className={styles.cardSub}>{sub}</p>}
        </div>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Segmented = ({ options, value, onChange, label }) => (
  <div className={styles.segmented} role="group" aria-label={label}>
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        className={`${styles.segBtn} ${value === o.value ? styles.segActive : ""}`}
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/**
 * Change against the previous period. Colour says good or bad, the arrow and
 * the words say which way, so it never rests on colour alone.
 */
export const Delta = ({ current, previous, goodWhenDown = false, suffix = "vs previous" }) => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return <span className={`${styles.delta} ${styles.deltaFlat}`}>&nbsp;</span>;
  }
  if (!previous) {
    return <span className={`${styles.delta} ${styles.deltaFlat}`}>{current ? `none ${suffix}` : " "}</span>;
  }
  const change = ((current - previous) / previous) * 100;
  const rounded = Math.round(change);
  if (rounded === 0) return <span className={`${styles.delta} ${styles.deltaFlat}`}>no change {suffix}</span>;
  const up = rounded > 0;
  const good = goodWhenDown ? !up : up;
  return (
    <span className={`${styles.delta} ${good ? styles.deltaGood : styles.deltaBad}`}>
      {up ? "▲" : "▼"} {Math.abs(rounded)}% {suffix}
    </span>
  );
};

export const StatTile = ({ label, value, sub, delta, spark }) => (
  <div className={styles.tile}>
    <span className={styles.tileLabel}>{label}</span>
    <span className={styles.tileValue}>{value}</span>
    {delta}
    <span className={styles.tileSub}>{sub}</span>
    {spark && spark.length > 1 && <Sparkline values={spark} />}
  </div>
);

export const Empty = ({ children }) => <div className={styles.empty}>{children}</div>;

/**
 * A table whose numeric columns sort on click. A column with `bar: true`
 * draws a small bar beside its value, scaled to the column's largest value.
 *
 * columns: [{ key, label, num, bar, render(row), sort(row) }]
 */
export const SortTable = ({ columns, rows, initialSort, rowKey, limit, empty = "Nothing in this period." }) => {
  const [sort, setSort] = useState(initialSort || { key: null, dir: "desc" });
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key);
    const value = (r) => (col && col.sort ? col.sort(r) : r[sort.key]);
    const copy = [...rows];
    copy.sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (typeof va === "string" || typeof vb === "string") {
        return sort.dir === "asc" ? String(va || "").localeCompare(String(vb || "")) : String(vb || "").localeCompare(String(va || ""));
      }
      return sort.dir === "asc" ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return copy;
  }, [rows, sort, columns]);
  const maxes = useMemo(() => {
    const m = {};
    columns.filter((c) => c.bar).forEach((c) => { m[c.key] = Math.max(0, ...rows.map((r) => Number(r[c.key]) || 0)); });
    return m;
  }, [rows, columns]);
  const shown = limit ? sorted.slice(0, limit) : sorted;

  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? styles.num : undefined} aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
                {c.sortable === false ? c.label : (
                  <button
                    type="button"
                    className={`${styles.sortBtn} ${sort.key === c.key ? styles.sortActive : ""}`}
                    onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === "desc" ? "asc" : "desc" }))}
                  >
                    {c.label}{sort.key === c.key ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={rowKey ? rowKey(r) : i}>
              {columns.map((c) => (
                <td key={c.key} className={c.num ? styles.num : c.className}>
                  {c.bar ? (
                    <span className={styles.cellBar}>
                      {c.render ? c.render(r) : fmtNum(r[c.key])}
                      <span className={styles.cellBarTrack} aria-hidden="true">
                        {maxes[c.key] > 0 && Number(r[c.key]) > 0 && (
                          <span className={styles.cellBarFill} style={{ width: `${(Number(r[c.key]) / maxes[c.key]) * 100}%` }} />
                        )}
                      </span>
                    </span>
                  ) : c.render ? c.render(r) : c.num ? fmtNum(r[c.key]) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const StateBadge = ({ state }) => {
  const map = {
    available: [styles.badgeAvailable, "Available"],
    sold: [styles.badgeSold, "Sold"],
    reserved: [styles.badgeReserved, "Reserved"],
    removed: [styles.badgeRemoved, "Gone from feed"],
  };
  const [cls, label] = map[state] || ["", "Status unknown"];
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
};
